<template>
  <div>
    <!-- Hero Banner -->
    <div class="hero-banner text-center text-white py-5">
      <h1 class="display-5 font-weight-bold" style="color: white;">ZESA's Monthly Secret: Don't Let Your Savings Disappear ⚡</h1>
      <p class="lead mt-3">Ever feel like you're paying too much for electricity? Well, <strong>there’s a secret hiding in ZESA's pricing system</strong>, and if you haven't bought any units this month, <strong>you're on the verge of missing out on some huge savings.</strong></p>
      <router-link to="/zesa" class="btn btn-lg btn-warning mt-4">Don't Miss Out - Buy ZESA Now</router-link>
    </div>

    <div class="container my-5">
      <!-- What Are Stepped Tariffs -->
      <section class="mb-5">
        <h2 class="section-heading">🔑 The ZESA Staircase That Resets Every Month</h2>
        <p class="text-muted" style="font-size: 1.1rem;">Think of ZESA tariffs like <strong>a staircase you climb every month</strong>. The first few steps are incredibly cheap - these are your initial, affordable units of electricity. But as you buy and use more, you climb higher, and each new step you climb costs more than the last. The key is that this entire staircase resets on the first of every month. That means at the start of each month, <strong>you get a fresh chance to buy power on those low, low bottom steps again.</strong></p>

        <!-- Staircase Graphic -->
        <div class="staircase-container">
          <div class="stair-step step1">1 – 50 Units<br /><span>$2.29</span></div>
          <div class="stair-step step2">51 – 100 Units<br /><span>$2.58</span></div>
          <div class="stair-step step3">101 – 200 Units<br /><span>$4.59</span></div>
          <div class="stair-step step4">201 – 300 Units<br /><span>$6.60</span></div>
          <div class="stair-step step5">301 – 400 Units<br /><span>$7.17</span></div>
          <div class="stair-step step6">400+ Units<br /><span>$7.39</span></div>
        </div>
        <p class="mt-3" style="font-size: 1.1rem;">As you can see, the higher the step, the more expensive it gets. That’s why timing your purchase matters!</p>
      </section>

      <!-- Why Buying Now Matters -->
      <section class="mb-5" style="font-size: 1.1rem;">
        <h2 class="section-heading">⏰ Why Buying <u>Now</u> Matters</h2>
        <p>Right now, if you haven't bought any electricity since the start of the month, you are standing at the <strong>bottom of the staircase</strong>. All those cheap first steps are just waiting for you to climb them.</p>
        <p class="mt-3">But here’s the thing: <strong>those steps won't be there forever.</strong> If you wait until the month ends, the entire staircase will reset. The opportunity to get this month's cheap units will be gone, forever. You'll have to start a new staircase next month, but you'll have missed out on the chance to double your savings by climbing two separate staircases this month and next.</p>

        <h2 class="section-heading mt-5">💰 The Smarter Way To <strong>Save</strong></h2>
        <p>This is where the real secret to maximizing your savings lies. Instead of buying all the electricity you plan to use next month, a smarter strategy is to split your purchase between this month and the next.</p>
        <p class="mt-3">This only applies if you haven't bought any ZESA units this month already, or if you have not used up all your cheaper steps. If you’re not sure, you can <strong><a href="https://selfservice.zetdc.co.zw/viewtoken" style="text-decoration: underline; font-weight: bold;">check when your last token purchase was</a></strong>. By splitting your purchase, you essentially get to "double-dip" on the cheapest tariffs, which will save you much more in the long run than buying all your units in a single month.</p>
        <p class="mt-3">For example, if you were going to buy 400 units of electricity anyway, it's far cheaper to buy 200 units now to take advantage of this month's lower-priced "steps." Then, at the start of next month, you can buy the other 200 units and get on those cheap steps all over again.</p>
        <ul class="savings-list">
          <li style="font-size: 1rem;"><strong>Buy 200 units this month</strong> → You enjoy the cheaper first steps.</li>
          <li style="font-size: 1rem;"><strong>Buy another 200 units next month</strong> → The steps reset, so you get the cheap units again.</li>
        </ul>

        <p>That’s <strong>400 cheap units</strong> across two months instead of paying for half at expensive rates if you wait until next month.</p>
      </section>

      <!-- Side by Side Comparison -->
      <section class="comparison-section mb-5">
        <h3 class="section-heading" style="font-size: 1.5rem;">📊 The Difference At a Glance</h3>
        <div class="row text-center">
          <div class="col-md-6">
            <div class="card saving-card p-4" >
              <h4>✅ Buy 200 Now + 200 Next Month</h4>
              <p class="price-highlight" >400 cheap units</p>
              <p>You climb the stairs twice from the bottom. More savings!</p>
              <router-link to="/zesa" class="btn btn-lg btn-success mt-4">Buy This Month's Units</router-link>
            </div>
          </div>
          <div class="col-md-6">
            <div class="card danger-card p-4">
              <h4>❌ Wait & Buy 400 Next Month</h4>
              <p class="price-warning">200 cheap + 200 expensive units</p>
              <p>You pay extra because you’re forced up the costly stairs.</p>
              <router-link to="/zesa" class="btn btn-lg btn-warning mt-4">Avoid The Costly Stairs</router-link>
            </div>
          </div>
        </div>
      </section>

      <!-- Call to Action -->
      <div class="cto-banner text-center py-5 px-2">
        <h2 class="font-weight-bold mb-5" style="color:white;">Don't Let This Month's Savings Slip Away!</h2>
        <p style="font-size: 1.1rem; color:snow; padding-bottom: 5px"><strong>Climb your staircase now before it's too late!</strong> Grab your ZESA tokens today and make your money go further.</p>
        <router-link to="/zesa" class="btn btn-lg btn-warning mt-3">Buy ZESA Now</router-link>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ZesaTariffsBlog",
};
</script>

<style scoped>
.hero-banner {
  background: linear-gradient(135deg, #ff9800, #f44336);
  border-radius: 12px;
  margin-left: 15px;
  margin-right: 15px;
}
.cto-banner {
  background: linear-gradient(135deg, #161a3d, #252c65);
  border-radius: 12px;
  margin-left: 15px;
  margin-right: 15px;
}
.section-heading {
  font-weight: bold;
  margin-bottom: 15px;
}
.staircase-container {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  margin: 30px 0;
  gap: 10px;
}
.stair-step {
  background: #f1f1f1;
  border: 2px solid #ddd;
  padding: 15px;
  width: 120px;
  text-align: center;
  font-weight: bold;
  border-radius: 6px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}
.step1 { height: 100px; background: #c8e6c9; }
.step2 { height: 140px; background: #dcedc8; }
.step3 { height: 180px; background: #fff9c4; }
.step4 { height: 220px; background: #ffe0b2; }
.step5 { height: 260px; background: #ffccbc; }
.step6 { height: 300px; background: #fd9778; }

.savings-list {
  list-style: none;
  padding: 0;
}
.savings-list li::before {
  content: "🔥 ";
}

.comparison-section .card {
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}
.saving-card {
  background: #e8f5e9;
  border-left: 6px solid #4caf50;
  margin-bottom: 10px;
}
.danger-card {
  background: #ffebee;
  border-left: 6px solid #f44336;
  margin-bottom: 10px;
}
.price-highlight {
  font-size: 1.5rem;
  font-weight: bold;
  color: #2e7d32;
}
.price-warning {
  font-size: 1.5rem;
  font-weight: bold;
  color: #c62828;
}
</style>
